import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ModalType = "error" | "success" | "warning" | "info";

type ModalVariant = "message" | "default";

import "@/styles/Modal.css";

interface ModalProps {
  show: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  type?: ModalType;
  variant?: ModalVariant;
  size?: "sm" | "md" | "lg";
  mobileCenter?: boolean;
  /** false: no close button and backdrop taps do nothing (a step the visitor must complete). */
  dismissible?: boolean;
}

export default function Modal({ show, onClose, children, title, type = "info", variant = "default", size = "md", mobileCenter = false, dismissible = true }: ModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  // The modal is portaled to <body>; this in-place anchor lets it inherit dir/lang from where it is mounted
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (show) {
      // Delay to allow DOM to render before adding show class
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Lock background scroll so iOS Safari keeps the modal in the visible viewport
  useEffect(() => {
    if (!show) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [show]);

  let borderColor = "#0070f3", icon = "ℹ️";
  if (type === "error") {
    borderColor = "#FF3B30"; icon = "❌";
  } else if (type === "success") {
    borderColor = "#34C759"; icon = "✅";
  } else if (type === "warning") {
    borderColor = "#FF9500"; icon = "⚠️";
  }

  if (!show) return null;

  const overlay = (
    <div
      className={`modal-overlay ${isVisible ? 'show' : ''} ${mobileCenter ? 'mobile-center-overlay' : ''}`}
      dir={anchor?.closest<HTMLElement>("[dir]")?.dir || undefined}
      lang={anchor?.closest<HTMLElement>("[lang]")?.lang || undefined}
      onClick={dismissible ? onClose : undefined}
    >
      <div
        className={`modal-content ${size} ${isVisible ? 'show' : ''} ${variant === "message" ? 'message-variant' : ''} ${mobileCenter ? 'mobile-center' : ''}`}
        style={variant === "message" ? { borderTop: `6px solid ${borderColor}` } : {}}
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 className="modal-title">{title}</h2>}
        {dismissible && (
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
          </button>
        )}
        {variant === "message" && (
          <div className="flex flex-col items-center text-center">
            <div className="modal-icon mb-4" style={{ fontSize: "2.5rem" }}>{icon}</div>
            <div className="w-full text-[#1D1D1F] dark:text-[#F5F5F7] font-medium leading-relaxed">
              {children}
            </div>
          </div>
        )}
        {variant !== "message" && <div className={title ? "pt-4" : ""}>{children}</div>}
      </div>
    </div>
  );

  return (
    <>
      <span ref={setAnchor} hidden />
      {anchor && createPortal(overlay, document.body)}
    </>
  );
}
